﻿import React, { useState, useEffect } from 'react';
import { FaTimes, FaSpinner } from 'react-icons/fa';
import { Provider, Facility, Specialization } from '../types';
import { apiCall } from '../config/api';

interface ProviderModalProps {
    isOpen: boolean;
    onClose: () => void;
    onSave: (providerData: Partial<Provider>) => Promise<void>;
    editProvider?: Provider | null;
    facilities?: Facility[];
    token?: string | null;
}

const ProviderModal: React.FC<ProviderModalProps> = ({ isOpen, onClose, onSave, editProvider = null, facilities = [], token = null }) => {
    const [formData, setFormData] = useState({
        first_name: '',
        last_name: '',
        credentials: '',
        provider_type: 'doctor',
        npi_number: '',
        email: '',
        phone: '',
        bio: '',
        education: '',
        languages: '',
        accepting_new_patients: true,
        years_of_experience: '',
        specializations: [] as number[],
        assigned_facilities: [] as number[],
        is_active: true,
        is_public: true,
        performs_procedures: false
    });

    const [specializations, setSpecializations] = useState<Specialization[]>([]);
    const [errors, setErrors] = useState<Record<string, string>>({});
    const [isLoading, setIsLoading] = useState(false);
    const [isLoadingSpecializations, setIsLoadingSpecializations] = useState(false);

    const providerTypes = [
        { value: 'doctor', label: 'Doctor' },
        { value: 'nurse', label: 'Nurse' },
        { value: 'physician_assistant', label: 'Physician Assistant' },
        { value: 'nurse_practitioner', label: 'Nurse Practitioner' },
        { value: 'therapist', label: 'Physiotherapist' },
        { value: 'specialist', label: 'Specialist' },
        { value: 'other', label: 'Other' }
    ];

    // Load specializations on component mount
    useEffect(() => {
        loadSpecializations();
    }, []);

    // Populate form when editing
    useEffect(() => {
        if (editProvider) {
            setFormData({
                first_name: editProvider.first_name || '',
                last_name: editProvider.last_name || '',
                credentials: editProvider.credentials || '',
                provider_type: editProvider.provider_type || 'doctor',
                npi_number: editProvider.npi_number || '',
                email: editProvider.email || '',
                phone: editProvider.phone || '',
                bio: editProvider.bio || '',
                education: editProvider.education || '',
                languages: editProvider.languages || '',
                accepting_new_patients: editProvider.accepting_new_patients ?? true,
                years_of_experience: editProvider.years_of_experience?.toString() || '',
                specializations: editProvider.specializations_details?.map(s => s.id) || [],
                assigned_facilities: editProvider.assigned_facilities || [],
                is_active: editProvider.is_active ?? true,
                is_public: editProvider.is_public ?? true,
                performs_procedures: editProvider.performs_procedures ?? false
            });
        } else {
            // Reset form for new provider
            setFormData({
                first_name: '',
                last_name: '',
                credentials: '',
                provider_type: 'doctor',
                npi_number: '',
                email: '',
                phone: '',
                bio: '',
                education: '',
                languages: '',
                accepting_new_patients: true,
                years_of_experience: '',
                specializations: [],
                assigned_facilities: [],
                is_active: true,
                is_public: true,
                performs_procedures: false
            });
        }
        setErrors({});
    }, [editProvider, isOpen]);

    const loadSpecializations = async () => {
        setIsLoadingSpecializations(true);
        try {
            const response = await apiCall('/providers/specializations/', {
                method: 'GET'
            }, token || undefined);
            if (response.ok) {
                const data = await response.json();
                setSpecializations(data.results || data);
            }
        } catch (error) {
            console.error('Failed to load specializations:', error);
        } finally {
            setIsLoadingSpecializations(false);
        }
    };

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
        const target = e.target;
        const name = target.name;
        const value = target.type === 'checkbox' && 'checked' in target ? target.checked : target.value;
        
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
        
        // Clear error when user starts typing
        if (errors[name]) {
            const newErrors = { ...errors };
            delete newErrors[name];
            setErrors(newErrors);
        }
    };

    const handleSpecializationChange = (specializationId: number) => {
        setFormData(prev => ({
            ...prev,
            specializations: prev.specializations.includes(specializationId)
                ? prev.specializations.filter(id => id !== specializationId)
                : [...prev.specializations, specializationId]
        }));
    };

    const handleFacilityChange = (facilityId: number) => {
        setFormData(prev => ({
            ...prev,
            assigned_facilities: prev.assigned_facilities.includes(facilityId)
                ? prev.assigned_facilities.filter(id => id !== facilityId)
                : [...prev.assigned_facilities, facilityId]
        }));
    };

    const validateForm = () => {
        const newErrors: Record<string, string> = {};
        
        if (!formData.first_name.trim()) {
            newErrors.first_name = 'First name is required';
        }
        
        if (!formData.last_name.trim()) {
            newErrors.last_name = 'Last name is required';
        }
        
        if (formData.email && !/\S+@\S+\.\S+/.test(formData.email)) {
            newErrors.email = 'Please enter a valid email address';
        }
        
        if (formData.years_of_experience && (isNaN(Number(formData.years_of_experience)) || Number(formData.years_of_experience) < 0)) {
            newErrors.years_of_experience = 'Years of experience must be a positive number';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        
        if (!validateForm()) {
            return;
        }

        setIsLoading(true);
        
        try {
            const submissionData = {
                ...formData,
                years_of_experience: formData.years_of_experience ? parseInt(formData.years_of_experience) : undefined
            };
            
            await onSave(submissionData);
        } catch (error) {
            console.error('Error saving provider:', error);
            setErrors({ submit: 'Failed to save provider. Please try again.' });
        } finally {
            setIsLoading(false);
        }
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-base-100 rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] flex flex-col">
                {/* Header */}
                <div className="flex justify-between items-center p-6 border-b border-base-300">
                    <h2 className="text-xl font-semibold">
                        {editProvider ? 'Edit Provider' : 'Add New Provider'}
                    </h2>
                    <button
                        onClick={onClose}
                        className="btn btn-ghost btn-sm btn-square"
                        disabled={isLoading}
                    >
                        <FaTimes />
                    </button>
                </div>

                {/* Form Content */}
                <div className="flex-1 overflow-y-auto p-6">
                    <form onSubmit={handleSubmit} className="space-y-6">
                        {/* Basic Information */}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div className="form-control">
                                <label className="label">
                                    <span className="label-text">First Name *</span>
                                </label>
                                <input
                                    type="text"
                                    name="first_name"
                                    value={formData.first_name}
                                    onChange={handleInputChange}
                                    className={`input input-bordered ${errors.first_name ? 'input-error' : ''}`}
                                    placeholder="Enter first name"
                                />
                                {errors.first_name && (
                                    <label className="label">
                                        <span className="label-text-alt text-error">{errors.first_name}</span>
                                    </label>
                                )}
                            </div>

                            <div className="form-control">
                                <label className="label">
                                    <span className="label-text">Last Name *</span>
                                </label>
                                <input
                                    type="text"
                                    name="last_name"
                                    value={formData.last_name}
                                    onChange={handleInputChange}
                                    className={`input input-bordered ${errors.last_name ? 'input-error' : ''}`}
                                    placeholder="Enter last name"
                                />
                                {errors.last_name && (
                                    <label className="label">
                                        <span className="label-text-alt text-error">{errors.last_name}</span>
                                    </label>
                                )}
                            </div>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                            <div className="form-control">
                                <label className="label">
                                    <span className="label-text">Credentials</span>
                                </label>
                                <input
                                    type="text"
                                    name="credentials"
                                    value={formData.credentials}
                                    onChange={handleInputChange}
                                    className="input input-bordered"
                                    placeholder="e.g., MD, DO, NP"
                                />
                            </div>

                            <div className="form-control">
                                <label className="label">
                                    <span className="label-text">Provider Type</span>
                                </label>
                                <select
                                    name="provider_type"
                                    value={formData.provider_type}
                                    onChange={handleInputChange}
                                    className="select select-bordered"
                                >
                                    {providerTypes.map(type => (
                                        <option key={type.value} value={type.value}>
                                            {type.label}
                                        </option>
                                    ))}
                                </select>
                            </div>

                            <div className="form-control">
                                <label className="label">
                                    <span className="label-text">Provider #</span>
                                </label>
                                <input
                                    type="text"
                                    name="npi_number"
                                    value={formData.npi_number}
                                    onChange={handleInputChange}
                                    className="input input-bordered"
                                    placeholder="Provider Number"
                                />
                            </div>
                        </div>

                        {/* Contact Information */}
                        <div className="divider">Contact Information</div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div className="form-control">
                                <label className="label">
                                    <span className="label-text">Email</span>
                                </label>
                                <input
                                    type="email"
                                    name="email"
                                    value={formData.email}
                                    onChange={handleInputChange}
                                    className={`input input-bordered ${errors.email ? 'input-error' : ''}`}
                                    placeholder="email@example.com"
                                />
                                {errors.email && (
                                    <label className="label">
                                        <span className="label-text-alt text-error">{errors.email}</span>
                                    </label>
                                )}
                            </div>

                            <div className="form-control">
                                <label className="label">
                                    <span className="label-text">Phone</span>
                                </label>
                                <input
                                    type="text"
                                    name="phone"
                                    value={formData.phone}
                                    onChange={handleInputChange}
                                    className="input input-bordered"
                                    placeholder="Phone number"
                                />
                            </div>
                        </div>

                        {/* Specializations */}
                        <div className="divider">Specializations</div>
                        <div className="form-control">
                            <label className="label">
                                <span className="label-text">Specializations</span>
                            </label>
                            {isLoadingSpecializations ? (
                                <div className="flex items-center justify-center p-4">
                                    <FaSpinner className="animate-spin mr-2" />
                                    <span>Loading specializations...</span>
                                </div>
                            ) : (
                                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2 p-4 border border-base-300 rounded-lg max-h-48 overflow-y-auto">
                                    {specializations.map(spec => (
                                        <label key={spec.id} className="label cursor-pointer justify-start">
                                            <input
                                                type="checkbox"
                                                className="checkbox checkbox-sm mr-2"
                                                checked={formData.specializations.includes(spec.id)}
                                                onChange={() => handleSpecializationChange(spec.id)}
                                            />
                                            <span className="label-text">{spec.name}</span>
                                        </label>
                                    ))}
                                </div>
                            )}
                        </div>

                        {/* Facility Assignment */}
                        {facilities.length > 0 && (
                            <>
                                <div className="divider">Facility Assignment</div>
                                <div className="form-control">
                                    <label className="label">
                                        <span className="label-text">Assigned Facilities</span>
                                    </label>
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2 p-4 border border-base-300 rounded-lg max-h-48 overflow-y-auto">
                                        {facilities.map(facility => (
                                            <label key={facility.id} className="label cursor-pointer justify-start">
                                                <input
                                                    type="checkbox"
                                                    className="checkbox checkbox-sm mr-2"
                                                    checked={formData.assigned_facilities.includes(facility.id)}
                                                    onChange={() => handleFacilityChange(facility.id)}
                                                />
                                                <span className="label-text">
                                                    {facility.name} ({facility.facility_type_details?.name || facility.facility_type_name || 'Clinic'})
                                                </span>
                                            </label>
                                        ))}
                                    </div>
                                </div>
                            </>
                        )}

                        {/* Status Options */}
                        <div className="divider">Status</div>
                        <div className="flex flex-wrap gap-4">
                            <label className="label cursor-pointer">
                                <input
                                    type="checkbox"
                                    name="accepting_new_patients"
                                    checked={formData.accepting_new_patients}
                                    onChange={handleInputChange}
                                    className="checkbox checkbox-primary mr-2"
                                />
                                <span className="label-text">Accepting New Patients</span>
                            </label>

                            <label className="label cursor-pointer">
                                <input
                                    type="checkbox"
                                    name="is_active"
                                    checked={formData.is_active}
                                    onChange={handleInputChange}
                                    className="checkbox checkbox-primary mr-2"
                                />
                                <span className="label-text">Active</span>
                            </label>

                            <label className="label cursor-pointer">
                                <input
                                    type="checkbox"
                                    name="is_public"
                                    checked={formData.is_public}
                                    onChange={handleInputChange}
                                    className="checkbox checkbox-primary mr-2"
                                />
                                <span className="label-text">Public Profile</span>
                            </label>

                            <label className="label cursor-pointer">
                                <input
                                    type="checkbox"
                                    name="performs_procedures"
                                    checked={formData.performs_procedures}
                                    onChange={handleInputChange}
                                    className="checkbox checkbox-primary mr-2"
                                />
                                <span className="label-text">Performs Procedures</span>
                            </label>
                        </div>

                        {errors.submit && (
                            <div className="alert alert-error">
                                <span>{errors.submit}</span>
                            </div>
                        )}
                    </form>
                </div>

                {/* Footer */}
                <div className="flex justify-end gap-3 p-6 border-t border-base-300">
                    <button
                        type="button"
                        onClick={onClose}
                        className="btn btn-ghost"
                        disabled={isLoading}
                    >
                        Cancel
                    </button>
                    <button
                        onClick={handleSubmit}
                        className="btn btn-primary"
                        disabled={isLoading}
                    >
                        {isLoading ? (
                            <>
                                <FaSpinner className="animate-spin mr-2" />
                                Saving...
                            </>
                        ) : (
                            editProvider ? 'Update Provider' : 'Create Provider'
                        )}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default ProviderModal;